import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_HOST } from 'environments/environment';

/**
 * API Service dla endpointów KSeF Admin
 */
@Injectable({ providedIn: 'root' })
export class KsefAdminApiService {

    constructor(private http: HttpClient) { }

    /**
     * GET /admin/ksef/batch-sessions
     * Lista sesji batch z filtrowaniem i paginacją
     */
    public getBatchSessions(params: {
        status?: string;
        userId?: string;
        email?: string;
        limit?: number;
        skip?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    }): Observable<any> {
        let httpParams = new HttpParams();
        
        if (params.status) httpParams = httpParams.set('status', params.status);
        if (params.userId) httpParams = httpParams.set('userId', params.userId);
        if (params.email) httpParams = httpParams.set('email', params.email);
        if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
        if (params.skip) httpParams = httpParams.set('skip', params.skip.toString());
        if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
        if (params.sortOrder) httpParams = httpParams.set('sortOrder', params.sortOrder);

        return this.http.get(`${API_HOST}admin/ksef/batch-sessions`, { params: httpParams });
    }

    /**
     * GET /admin/ksef/batch-sessions/:id/details
     * Szczegóły sesji batch (faktury + timeline + logi)
     */
    public getBatchSessionDetails(sessionId: string): Observable<any> {
        return this.http.get(`${API_HOST}admin/ksef/batch-sessions/${sessionId}/details`);
    }

    /**
     * GET /admin/ksef/batch-sessions/history
     * Historia wszystkich wysyłek (activity logs)
     */
    public getBatchSessionsHistory(params: {
        logType?: string;
        userId?: string;
        sessionRef?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        skip?: number;
    }): Observable<any> {
        let httpParams = new HttpParams();
        
        if (params.logType) httpParams = httpParams.set('logType', params.logType);
        if (params.userId) httpParams = httpParams.set('userId', params.userId);
        if (params.sessionRef) httpParams = httpParams.set('sessionRef', params.sessionRef);
        if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
        if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);
        if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
        if (params.skip) httpParams = httpParams.set('skip', params.skip.toString());

        return this.http.get(`${API_HOST}admin/ksef/batch-sessions/history`, { params: httpParams });
    }

    /**
     * POST /admin/ksef/batch-sessions/:id/retry
     * Retry sesji batch (reset checkAttempts)
     */
    public retryBatchSession(sessionId: string): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/batch-sessions/${sessionId}/retry`, {});
    }

    /**
     * POST /admin/ksef/batch-sessions/:id/resend-all
     * Ponowne wysłanie wszystkich faktur z sesji batch
     */
    public resendAllInvoicesFromSession(sessionId: string, immediate = true): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/batch-sessions/${sessionId}/resend-all`, { immediate });
    }

    /**
     * POST /admin/ksef/invoices/:documentId/resend
     * Ponowne wysłanie pojedynczej faktury
     */
    public resendSingleInvoice(documentId: string, immediate = true): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/invoices/${documentId}/resend`, { immediate });
    }

    /**
     * GET /admin/ksef/validation-errors
     * Błędy walidacji/mapowania faktur PRZED wysyłką
     */
    public getValidationErrors(params: {
        limit?: number;
        skip?: number;
        userId?: string;
        documentId?: string;
        from?: string;
        to?: string;
    }): Observable<any> {
        let httpParams = new HttpParams();
        
        if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
        if (params.skip) httpParams = httpParams.set('skip', params.skip.toString());
        if (params.userId) httpParams = httpParams.set('userId', params.userId);
        if (params.documentId) httpParams = httpParams.set('documentId', params.documentId);
        if (params.from) httpParams = httpParams.set('from', params.from);
        if (params.to) httpParams = httpParams.set('to', params.to);

        return this.http.get(`${API_HOST}admin/ksef/validation-errors`, { params: httpParams });
    }

    /**
     * POST /admin/ksef/clear-test-data
     * Usuwa wszystkie dane KSeF z środowiska testowego (ksefEnvironment='test')
     */
    public clearTestData(): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/clear-test-data`, {});
    }

    /**
     * POST /admin/ksef/run-lazy-update
     * Uruchomienie migracji Unified Document Store (lazy update pól dokumentów)
     */
    public runLazyUpdateDocumentFields(): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/run-lazy-update`, {});
    }

    /**
     * GET /admin/ksef/environment
     * Sprawdza środowisko KSeF admina (do blokowania narzędzi testowych)
     */
    public getAdminKsefEnvironment(): Observable<any> {
        return this.http.get(`${API_HOST}admin/ksef/environment`);
    }

    /**
     * POST /admin/ksef/send-test-invoice
     * Wysyła testową fakturę do KSeF (ZABLOKOWANE na produkcji!)
     */
    public sendTestInvoice(document: any): Observable<any> {
        return this.http.post(`${API_HOST}admin/ksef/send-test-invoice`, { document });
    }
}

